import SafeImage from '@ui/SafeImage';
import { cn } from '@util/index';
import type { ProductLight } from 'models/product';
import type React from 'react';

export const ProductVariationImages = ({
  product,
  activeImageIndex,
  onSelect,
  onHover,
}: {
  product: ProductLight;
  activeImageIndex?: number;
  onSelect?: (index: number, e: React.MouseEvent) => void;
  onHover?: (index: number | null) => void;
}) => {
  const uniqueVariantImages = product.variation_images;

  const images = new Set(uniqueVariantImages);

  if (images.size === 0) return null;

  const findImageIndex = (variationImage: string): number => {
    return (
      product.variation_images?.findIndex((img) => {
        const normalizeUrl = (url: string) =>
          url.replace(/\/(200|500|1000)_max\//g, '/');
        return normalizeUrl(img) === normalizeUrl(variationImage);
      }) ?? -1
    );
  };

  return (
    <div
      className="flex items-center gap-x-2 rounded-[1.5rem] bg-gradient-to-t from-white to-transparent p-2"
      onMouseLeave={() => onHover?.(null)}
    >
      <div className="flex items-center gap-x-2">
        {Array.from(images)
          .slice(0, 4)
          .map((image, index) => {
            if (image === '' || image === undefined) return;

            let optimizedImage = image;
            if (image.startsWith('https://cdn.wpsstatic.com/images/')) {
              optimizedImage = image.replace(
                /\/(500|1000)_max\//g,
                '/200_max/'
              );
            }

            const imageIndex = findImageIndex(image);
            const isActive = activeImageIndex === imageIndex;

            return (
              <div
                className={cn(
                  'h-[4rem] w-[4rem] cursor-pointer rounded-[1rem] bg-white p-1 transition-opacity duration-150 ease-out hover:opacity-100 sm:h-[5rem] sm:w-[5rem]',
                  index === 3 && 'hidden sm:block',
                  isActive
                    ? 'border-2 border-brand-secondary opacity-100'
                    : 'border border-zinc-200 opacity-75 hover:border-zinc-400'
                )}
                key={image}
                onClick={(e) => onSelect?.(imageIndex, e)}
                onMouseEnter={() => onHover?.(imageIndex)}
              >
                <SafeImage
                  src={optimizedImage}
                  alt={product.title}
                  height={100}
                  width={100}
                  className="h-full w-full object-contain"
                />
              </div>
            );
          })}
      </div>

      {images.size > 4 && (
        <div
          className={`flex h-[4rem] w-[4rem] shrink-0 items-center justify-center rounded-[1rem] border border-zinc-300 bg-white p-2 font-semibold text-black sm:h-[5rem] sm:w-[5rem]`}
        >
          +{images.size - 4}
        </div>
      )}
    </div>
  );
};
